import type { BillingInvoiceSummary } from '@fruitful/types';
import { useEffect, useState } from 'react';

import useGetBillingInvoices from './useGetBillingInvoices';

interface LoadedInvoicePage {
    cursorKey: string;
    invoices: BillingInvoiceSummary[];
    nextCursor: string | null;
}

export const useBillingInvoicePages = (userId: string) => {
    const [requestedCursor, setRequestedCursor] = useState<string | null>(null);
    const [pages, setPages] = useState<LoadedInvoicePage[]>([]);

    const {
        data: invoicePage,
        isLoading,
        isError,
        isFetching,
        refetch,
    } = useGetBillingInvoices({
        userId,
        cursor: requestedCursor,
    });

    const cursorKey = requestedCursor ?? '';

    useEffect(() => {
        if (invoicePage == null) {
            return;
        }

        setPages((current) => {
            const nextPage: LoadedInvoicePage = {
                cursorKey,
                invoices: invoicePage.invoices,
                nextCursor: invoicePage.nextCursor,
            };
            const index = current.findIndex(
                (page) => page.cursorKey === cursorKey,
            );
            if (index === -1) {
                return [...current, nextPage];
            }
            const updated = [...current];
            updated[index] = nextPage;
            return updated;
        });
    }, [invoicePage, cursorKey]);

    const invoices = pages.flatMap((page) => page.invoices);
    const nextCursor =
        pages.length === 0
            ? null
            : (pages[pages.length - 1]?.nextCursor ?? null);

    return {
        invoices,
        nextCursor,
        isInitialLoading: isLoading && pages.length === 0,
        isError,
        isFetching,
        refetch,
        loadMore: () => {
            if (nextCursor != null) {
                setRequestedCursor(nextCursor);
            }
        },
    };
};
